import {
  ExtractionResponse,
  ProcessedExtractionData,
  TokenCostInfo,
  ProcessedFieldItem
} from "../models/document-extraction.model";

/**
 * Utility class for processing document extraction data
 */
export class ExtractionDataProcessor {
  private static readonly TOKEN_COST_FIELD = "total token cost";

  private static readonly CONFIDENCE_SUFFIX = "_confidence";

  private static readonly SCORE_PREFIX = "Score_";

  /**
   * Checks if a key is a score key (starts with "Score_")
   */
  private static isScoreKey(key: string): boolean {
    return key.toLowerCase().startsWith(this.SCORE_PREFIX.toLowerCase());
  }

  /**
   * Extracts field data from extraction response, excluding confidence fields
   */
  static extractFields(data: ExtractionResponse[]): ProcessedExtractionData {
    return data.reduce((acc: ProcessedExtractionData, item: any) => {
      Object.keys(item).forEach((key) => {
        if (!key.includes(this.CONFIDENCE_SUFFIX)) {
          acc[key] = item[key];
        }
      });
      return acc;
    }, {});
  }

  /**
   * Extracts token cost information from extraction response
   */
  static extractTokenCost(data: ExtractionResponse[]): TokenCostInfo | null {
    const tokenCostItem = data.find((item: any) =>
      Object.keys(item).some(
        (key) => key.toLowerCase() === this.TOKEN_COST_FIELD
      )
    );

    if (tokenCostItem) {
      const tokenCostKey = Object.keys(tokenCostItem).find(
        (key) => key.toLowerCase() === this.TOKEN_COST_FIELD
      );

      if (tokenCostKey) {
        return {
          cost: tokenCostItem[tokenCostKey],
          fieldName: tokenCostKey
        };
      }
    }

    return null;
  }

  /**
   * Processes extraction data into the format expected by the UI
   */
  static processExtractionData(
    data: ExtractionResponse[]
  ): ProcessedFieldItem[] {
    return data
      .map((item: any) => {
        const processedItem: any = {};

        Object.keys(item).forEach((key) => {
          if (key.includes(this.CONFIDENCE_SUFFIX)) {
            // Convert confidence field to Score_ format
            const fieldName = key.replace(this.CONFIDENCE_SUFFIX, "");
            const confidenceValue = item[key];
            processedItem[`${this.SCORE_PREFIX}${fieldName}`] =
              typeof confidenceValue === "number"
                ? `${confidenceValue}%`
                : confidenceValue;
          } else {
            processedItem[key] = item[key];
          }
        });

        return processedItem;
      })
      .filter((item: any) => {
        const key = Object.keys(item).find((k) => !this.isScoreKey(k));
        return key && key.toLowerCase() !== this.TOKEN_COST_FIELD;
      });
  }

  /**
   * Formats container name according to business rules
   */
  static formatContainerName(companyName: string): string {
    return companyName.toLowerCase().replace(/[^a-z0-9]/g, "-");
  }

  /**
   * Validates if extraction model is valid for processing
   */
  static isValidModel(selectedModel: string): boolean {
    return selectedModel !== "Select Model" && selectedModel.trim() !== "";
  }
}
